import { useCallback, useEffect, useRef, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/RoutesTranslated';

import { useLocation, useNavigate, type Location, NavigateFunction } from 'react-router-dom';

import { useScreenSize, ScreenSize } from '@/ui/hooks/useScreenSize';

import { Container, Row, Col, Button } from 'react-bootstrap';

import '@/ui/containers/userOptions/UserOptionsContainer/style.css';

import { useAppSelector } from '@/ui/store/helperRedux';

import UserController from '@/controllers/UserController';
import { translate } from '@/domain/utils/translate';

import BackClose from '@/ui/components/BackClose';
import Breadcrumbs from '@/ui/components/Breadcrumbs';
import IconList from '@/ui/components/IconList';
import InputForm from '@/ui/components/forms/InputForm';
import AlertModal from '@/ui/components/modals/AlertModal';
import PasswordInput from '@/ui/components/user/PasswordInput';

/** Componente From para cambio de contraseña
 * @component
 */


const PasswordResetForm = (): JSX.Element => {
    /** Hook para generar una navegación dentro de la aplicación. */
    const navigate: NavigateFunction = useNavigate();

    /** Hook para trabajar con document location  */
    const location: Location = useLocation();

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();

    /** Estado Redux del usuario */
    const userLogued = useAppSelector((state) => state.userState);
    /** Controlador para usuario */
    const { recoverPassword } = UserController();

    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /** Rutas URLs */
    const routesDirections = getRoutesDirections();

    /** Estado para almacenar el estado de la alerta */
    const [alert, setAlert] = useState<boolean>(false);

    /** Declaración inicial de los botones como deshabilitado    */
    const [stateButton, setStateButton] = useState<string>('btn-primary-icon-standard disabled');

    /** Declaración inicial de los mensajes de error como vacios    */
    const [messageError, setMessageError] = useState<string>('');

    /** Declaración inicial un input en su estado normal */
    const [inputWithError, setInputWithError] = useState<boolean>(false);
    /** Referencia al input de contraseña */
    const passwordRef = useRef<HTMLInputElement>(null);
    /** Referencia al input de email */
    const emailRef = useRef<HTMLInputElement>(null);

    /** Estado para almacenar el recoveryCode que viene del local storage*/
    const [recoveryCode, setRecoveryCode] = useState<string>('');

    const INVALIDATE_CODE = translate('session.time_code_alert');
    const RECOVERY_TITLE: string = translate('session.reset_password_title');
    const VERIFY_TEXT: string = translate('session.verify_text');
    const EMAIL_LABEL: string = translate('session.email');

    /** Deshabilita un botón para avanzar a la siguiente fase */
    const disableButton = useCallback(() => {
        setStateButton('btn-primary-text-standard disabled');
    }, []);
    /** Deshabilita un botón para avanzar a la siguiente fase */
    const enableButton = useCallback(() => {
        setStateButton('btn-primary-text-standard');
    }, []);

    /** useEffect para traer el email por referencia de la modal de recuperar contraseña */
    useEffect(() => {
        if (emailRef.current) {
            emailRef.current.value = userLogued.user.email ?? '';
        }
    }, [userLogued.user.email]);

    /** useEffect para traer el recoveryCode del local storage */
    useEffect(() => {
        const storedRecoveryCode = localStorage.getItem('recoveryCode');
        if (storedRecoveryCode) {
            setRecoveryCode(storedRecoveryCode);
        }
    }, []);

    /** useEffect para habiliar el botón de envío */
    useEffect(() => {
        if (inputWithError) {
            disableButton();
        } else {
            if (passwordRef.current?.value && passwordRef.current?.value.length > 0) {
                enableButton();
            }
        }
    }, [inputWithError, disableButton, enableButton]);

    /** Función para manejar el cambio de contraseña */
    const handleChangePassword = async (): Promise<void> => {
        disableButton();

        try {
            const changePassword = await recoverPassword(userLogued.user.email ?? '', recoveryCode, passwordRef.current?.value ?? '');
            if (changePassword) {
                setAlert(true);
                localStorage.setItem('alert', JSON.stringify(true));
                localStorage.removeItem('alert');
                navigate(routesDirections.CHANGE_PASSWORD_ROUTE, { state: { originURL: location.pathname, alert: true } });

            } else {
                setInputWithError(true);
                setMessageError(INVALIDATE_CODE);

            }
            localStorage.removeItem('recoveryCode');

        } catch (error) {
            console.error(error);
        }

        if (!Error) {
            enableButton();
        }
    };


    return (

        <Container fluid className='p-0 g-0'>
            <Row className='g-0'>
                {isMobile && (
                    <BackClose classMain='justify-content-between py-2'>
                        <Breadcrumbs />
                    </BackClose>
                )}
                <Col lg={12} className=' d-flex justify-content-center'>
                    <Col lg={7} xs={11} >

                        <Col className={isMobile ? 'd-flex flex-column pt-4' : ' d-flex flex-column mt-5 pt-5 '}>
                            <IconList iconName='ico-key' title={RECOVERY_TITLE} classTitle='text-secondary-1 h3' classIcon='svg-primary-3 pe-2'></IconList>
                        </Col>

                        <InputForm
                            className='mb-3 mt-4 '
                            label={EMAIL_LABEL}
                            classLabel='paragraph text-secondary-2 '
                            id='input-reset-password-email'
                            inputRef={emailRef}
                            type='email'
                            iconName='ico-error'
                            disabled
                        />
                        <PasswordInput
                            passwordRef={passwordRef}
                            email={emailRef.current?.value ?? ''}
                            passwordInputWithError={inputWithError}
                            setPasswordInputWithError={setInputWithError}
                        />

                        {messageError ? (

                            <span className='text-danger p-regular '>{messageError}</span>
                        ) : (
                            <Col className='pb-4' />
                        )}

                        <Button className={`w-100 mb-5 py-3  ${stateButton}`} type='button' onClick={handleChangePassword}>
                            <span className='p-large text-primary-4 ' >{VERIFY_TEXT}</span>
                        </Button>
                        {alert &&
                            <AlertModal
                                hideAlert={() => setAlert(false)}
                                type='success'
                                hide={5000}
                                content={translate('user.updated_text')}
                            />
                        }
                    </Col>
                </Col>
            </Row>

        </Container >

    );
};

export default PasswordResetForm;
